import { Box, Button, makeStyles, Typography } from "@material-ui/core";
import React, { lazy } from "react";
import { useTranslation } from "react-i18next";
import { MdCheck, MdInfoOutline, MdPriorityHigh } from "react-icons/md";
import { Link, Navigate, useLocation } from "react-router-dom";

import { logout } from "../../model/oidc/actions";
import { LanguageEnum, Me } from "../../types/api-schema";
import { useAppDispatch, useAppSelector } from "../../utils/customHooks";

import "../../styles/tailwind.css";

const Wizard = lazy(() => {
  return import("../Wizard");
});

export const WIZARD_PATHNAME = "/wizard";
export const BILLING_PATHNAME = "/billing";

const useStyles = makeStyles(() => {
  return {
    root: {
      alignItems: "center",
      display: "flex",
      flexDirection: "column",
      gap: 24,
      justifyContent: "center",
      minHeight: "100vh",
      padding: 24,
      textAlign: "center",
    },
  };
});

type AccessRedirect =
  | "billing"
  | "contact_admin"
  | "deactivated"
  | "wizard"
  | null;

/** Where a logged-in Keycloak user has to go instead of the editor. */
export function getAccessRedirect(me?: Me | null): AccessRedirect {
  if (!me || me.auth_provider !== "keycloak") {
    return null;
  }
  switch (me.state) {
    case "deactivated":
      return "deactivated";
    case "free_period_expired":
      return me.organizations.some((m) => {
        return m.status === "active" && m.role === "admin";
      })
        ? "billing"
        : "contact_admin";
    case "no_organization":
      return "wizard";
    default:
      return null;
  }
}

export function AccessNotice({
  children,
  text,
  title,
}: {
  children?: React.ReactNode;
  text: string;
  title?: string;
}) {
  const { t } = useTranslation();
  const classes = useStyles();
  const dispatch = useAppDispatch();
  return (
    <Box className={classes.root}>
      {title && <Typography variant="h1">{title}</Typography>}
      <Typography>{text}</Typography>
      {children}
      <Button
        onClick={() => {
          dispatch(logout());
        }}
        variant="outlined"
      >
        {t("Abmelden")}
      </Button>
    </Box>
  );
}

export function FreePeriodExpiredPage() {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const me = useAppSelector((state) => {
    return state.oidc.me;
  });
  const canUpgrade = getAccessRedirect(me) === "billing";
  return (
    <main className="box-border flex h-screen w-full flex-col items-center overflow-y-auto bg-[#f5f7fa] p-2 font-body tracking-normal text-text-strong supports-[height:100dvh]:h-dvh sm:p-6">
      <div className="my-auto w-full max-w-[548px] shrink-0">
        <section
          aria-labelledby="free-period-expired-title"
          className={`box-border rounded-md border border-solid border-[#e0e8f0] bg-white px-[21px] py-7 ${canUpgrade ? "min-h-[390px]" : "min-h-60"}`}
        >
          <div
            aria-hidden="true"
            className={`mb-4 flex size-10 items-center justify-center rounded-full ${canUpgrade ? "bg-amber-50 text-[#c66a16]" : "bg-neutral-100 text-text-muted"}`}
          >
            {canUpgrade ? (
              <MdPriorityHigh size={20} />
            ) : (
              <MdInfoOutline size={20} />
            )}
          </div>
          <h1
            className="m-0 text-[17px] leading-6 font-bold"
            id="free-period-expired-title"
          >
            {t(
              canUpgrade
                ? "Deine kostenlose Phase ist abgelaufen"
                : "Kein Zugang zum Editor",
            )}
          </h1>
          <p className="mt-2 mb-0 max-w-[340px] text-[12px] leading-4.5 text-text-muted">
            {t(
              canUpgrade
                ? "Deine Karten sind gespeichert und wieder verfügbar, sobald du einen Plan wählst."
                : "Die kostenlose Phase deiner Organisation ist abgelaufen. Bitte wende dich an deinen Admin.",
            )}
          </p>
          {canUpgrade && (
            <>
              <div className="mt-6 grid max-w-[408px] grid-cols-1 gap-3 min-[480px]:grid-cols-[1.1fr_1fr]">
                <Link
                  className="box-border inline-flex min-h-10 items-center justify-center rounded-sm bg-primary px-4 py-2 text-center text-[13px] leading-5 font-bold text-white no-underline hover:bg-primary-hover focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary"
                  to={BILLING_PATHNAME}
                >
                  {t("Plan wählen")}
                </Link>
                <Link
                  className="box-border inline-flex min-h-10 items-center justify-center rounded-sm border border-solid border-[#e0e8f0] bg-white px-4 py-2 text-center text-[13px] leading-5 font-bold text-text-strong no-underline hover:bg-primary-subtle focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary"
                  to={BILLING_PATHNAME}
                >
                  {t("Abo & Rechnungen")}
                </Link>
              </div>
              <hr className="mt-6 mb-4 border-0 border-t border-solid border-[#e0e8f0]" />
              <h2 className="m-0 text-[12px] leading-4 font-bold">
                {t("Weiterhin erreichbar")}
              </h2>
              <ul className="mt-2 mb-0 grid list-none gap-2 p-0 text-[12px] leading-4 text-text-muted">
                {[
                  t("Mein Konto"),
                  t("Abo & Rechnungen"),
                  t("Geteilte Karten"),
                ].map((label) => {
                  return (
                    <li className="flex items-center gap-2" key={label}>
                      <MdCheck
                        aria-hidden="true"
                        className="shrink-0 text-success"
                        size={16}
                      />
                      {label}
                    </li>
                  );
                })}
              </ul>
            </>
          )}
        </section>
        <button
          className="mx-auto mt-4 block cursor-pointer rounded-sm border-0 bg-transparent px-3 py-2 font-body text-[12px] leading-4 text-text-muted hover:text-text-strong focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary"
          onClick={() => {
            dispatch(logout());
          }}
          type="button"
        >
          {t("Abmelden")}
        </button>
      </div>
    </main>
  );
}

/**
 * Renders the editor only if the user's organization access allows it,
 * otherwise a notice or a redirect to the wizard or billing page.
 */
function AccessGate({
  children,
  ...props
}: {
  [key: string]: unknown;
  children: React.ReactNode;
}) {
  const { t } = useTranslation();
  const { hash, search } = useLocation();
  const me = useAppSelector((state) => {
    return state.oidc.me;
  });

  const redirect = getAccessRedirect(me);

  switch (redirect) {
    case "billing":
    case "contact_admin":
      return <FreePeriodExpiredPage />;
    case "deactivated":
      return (
        <AccessNotice
          text={t(
            "Dein Zugang zu dieser Organisation ist deaktiviert. Bitte wende dich an deinen Admin.",
          )}
        />
      );
    case "wizard":
      return (
        <Navigate replace to={{ hash, pathname: WIZARD_PATHNAME, search }} />
      );
    default:
      // Forward the props App passes to its route element
      return (
        <>
          {React.Children.map(children, (child) => {
            return React.isValidElement(child)
              ? React.cloneElement(child, props)
              : child;
          })}
        </>
      );
  }
}

/** Placeholder for the registration wizard. */
export function WizardPage() {
  const { hash, search } = useLocation();
  const me = useAppSelector((state) => {
    return state.oidc.me;
  });
  const parameters = new URLSearchParams(search);
  const preview = parameters.get("preview") === "true";
  const requestedLanguage = parameters
    .get("lang")
    ?.toLowerCase()
    .replace("_", "-")
    .split("-")[0];
  const initialLanguage = Object.values(LanguageEnum).find((language) => {
    return language === requestedLanguage;
  });
  if (!preview && getAccessRedirect(me) !== "wizard") {
    return <Navigate replace to={{ hash, pathname: "/", search }} />;
  }
  return <Wizard initialLanguage={initialLanguage} preview={preview} />;
}

/** Placeholder for the billing page. */
export function BillingPage() {
  const { t } = useTranslation();
  const me = useAppSelector((state) => {
    return state.oidc.me;
  });
  const activeFreePeriod =
    me?.state === "ok" &&
    me.organizations.some(({ organization, status }) => {
      return (
        status === "active" &&
        organization.plan === "free" &&
        organization.free_period_expired === false
      );
    });
  if (getAccessRedirect(me) !== "billing" && !activeFreePeriod) {
    return <Navigate replace to="/" />;
  }
  return (
    <AccessNotice
      text={t(
        activeFreePeriod
          ? "Wähle einen Plan, um deine Organisation upzugraden."
          : "Die Testphase deiner Organisation ist abgelaufen. Wähle einen Plan, um weiterzuarbeiten.",
      )}
      title={t("Upgrade")}
    />
  );
}

export default React.memo(AccessGate);
